import React, {useState} from 'react';

import TabbedModal, {type TabType} from 'app/components/general/TabbedModal';
import {createFormErrorsContext, type FormErrorsProps} from 'app/general/forms';
import {useUpdatePolicy} from 'app/policies/api';
import {useUpdateProcedure} from 'app/procedures/api';
import {useAccountSettingsState} from 'app/settings/api';
import {useInviteUser} from 'app/user/api';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {ShareProcessModal} from './ShareProcessModal';
import {SharePublicModalPane} from './SharePublicModalPane';
import {ShareQRCodeModalPane} from './ShareQRCodeModalPane';
import {ShareWithTeammateModalPane} from './ShareWithTeammateModalPane';

import type {MinimumActionableTypeWithDocument} from './types';
import type {NewInvitationType, ShareableContentType} from 'app/user/types';

interface BaseShareModalProps {
  hideTeammateTab?: boolean;
  hidePublicTab?: boolean;
  hideQRCodeTab?: boolean;
  show: boolean;
  contentType: ShareableContentType;
  onClose: () => void;
  initiallySelectedTab?: 'show-public' | 'show-qrcode' | 'show-team';
  object: MinimumActionableTypeWithDocument;
}

interface Props extends FormErrorsProps, BaseShareModalProps {
  actionCallback: (minimalCallbackObject: {
    url: string;
    isPrivate: boolean;
  }) => void;
}

const BaseShareDocumentModal = createFormErrorsContext<Props>(
  ({
    onClose,
    object,
    contentType,
    show,
    initiallySelectedTab,
    hideTeammateTab = false,
    hidePublicTab = false,
    hideQRCodeTab = false,
    setFormErrors,
    actionCallback,
  }: Props) => {
    const [inviteUser] = useInviteUser();

    const {STATIC_URL} = useAccountSettingsState().data!;

    function shareWithTeammate(invitations: Array<NewInvitationType>) {
      return inviteUser({invitations})
        .unwrap()
        .then(
          (response) => {
            setFormErrors({});
            onClose();
            return response;
          },
          (err) => {
            let formErrors = err.data[0];

            if (formErrors['to_user_id']) {
              formErrors.name = [gt('Please select existing teammembers')];
            }

            setFormErrors(formErrors);
          }
        );
    }

    const tabs: Array<TabType> = [];

    if (!hidePublicTab) {
      tabs.push({
        eventKey: 'show-public',
        name: gt('Share Publicly'),
        icon: 'fa fa-globe-americas',
        content: (
          <SharePublicModalPane
            object={object}
            onChange={actionCallback}
            onCancel={onClose}
            staticURL={STATIC_URL}
          />
        ),
      });
    }

    if (hideTeammateTab === false) {
      tabs.push({
        eventKey: 'show-team',
        name: gt('Share with Teammates'),
        icon: 'fa fa-users',
        content:
          object != null ? (
            <ShareWithTeammateModalPane
              objectId={
                object.content_type === 'document'
                  ? object.object_id
                  : object.id
              }
              name={object.name}
              contentType={contentType}
              onConfirm={shareWithTeammate}
              onCancel={onClose}
            />
          ) : null,
      });
    }

    if (!hideQRCodeTab) {
      tabs.push({
        eventKey: 'show-qrcode',
        name: gt('QR Code'),
        icon: 'fas fa-qrcode',
        content: <ShareQRCodeModalPane object={object} onCancel={onClose} />,
      });
    }

    return (
      <TabbedModal
        show={show}
        onCancel={onClose}
        tabs={tabs}
        initiallySelectedTab={initiallySelectedTab}
      />
    );
  }
);

function ProcedureShareDocumentModal(props: BaseShareModalProps) {
  const [updateProcedure] = useUpdateProcedure();

  const setProcedurePrivate = ({url, isPrivate}) =>
    updateProcedure({
      url,
      private: isPrivate,
      successMessage: isPrivate
        ? gt('This procedure is now private')
        : gt('This procedure is now public'),
    });

  return (
    <BaseShareDocumentModal {...props} actionCallback={setProcedurePrivate} />
  );
}

function PolicyShareDocumentModal(props: BaseShareModalProps) {
  const [updatePolicy] = useUpdatePolicy();

  const setPolicyPrivate = ({url, isPrivate}) =>
    updatePolicy({
      url,
      private: isPrivate,
      successMessage: isPrivate
        ? gt('This policy is now private')
        : gt('This policy is now public'),
    });

  return (
    <BaseShareDocumentModal {...props} actionCallback={setPolicyPrivate} />
  );
}

function ProcessShareDocumentModal(props: BaseShareModalProps) {
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processModalKey, setProcessModalKey] = useState(0);

  const setProcessPrivate = () => {
    setProcessModalKey((key) => key + 1);
    setShowProcessModal(true);
    return Promise.reject(new Error(gt('confirmation pending')));
  };

  return (
    <>
      <BaseShareDocumentModal {...props} actionCallback={setProcessPrivate} />
      <ShareProcessModal
        show={showProcessModal}
        key={processModalKey}
        close={() => setShowProcessModal(false)}
        url={props.object.url}
        processName={
          <b>{getObjectName(props.object, props.contentType, true)}</b>
        }
        isPrivate={props.object.private}
      />
    </>
  );
}
const ShareModalPerDocument = {
  procedure: ProcedureShareDocumentModal,
  proceduregroup: ProcessShareDocumentModal,
  policy: PolicyShareDocumentModal,
};

interface ShareModalProps extends BaseShareModalProps {
  actionCallback?: (minimalCallbackObject: {
    url: string;
    isPrivate: boolean;
  }) => void;
}

export function ShareDocumentModal({
  actionCallback,
  ...props
}: ShareModalProps) {
  if (actionCallback == null) {
    const ShareModalComponent = ShareModalPerDocument[props.contentType];
    return <ShareModalComponent {...props} />;
  }

  return <BaseShareDocumentModal {...props} actionCallback={actionCallback} />;
}
